import type { LibraryItem } from "@excalidraw/excalidraw/types";
import type { OrderedExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import { isChineseLocale, type LocaleCode } from "@/shared/types/surface";
import { getTranslations } from "@/i18n/translations";
import communityData from "./communityLibraries.json";

export type SubjectCategoryId =
  | "physics"
  | "math"
  | "chemistry"
  | "cs"
  | "chinese";

export interface LibraryCategoryMeta {
  id: SubjectCategoryId;
  label: string;
  sourceKeys: string[];
  count: number;
}

const rawCommunityItems = communityData as unknown as (LibraryItem & { id: string })[];

interface BaseSubjectCategoryMeta {
  readonly id: SubjectCategoryId;
  readonly sourceKeys: string[];
  readonly count: number;
}

// Core 4 STEM disciplines visible in all locales
const BASE_SUBJECT_CATEGORIES: readonly BaseSubjectCategoryMeta[] = [
  {
    id: "physics",
    sourceKeys: ["circuit-components", "logic-gates"],
    count: rawCommunityItems.filter(
      (i) => i.id.includes("circuit-components") || i.id.includes("logic-gates")
    ).length,
  },
  {
    id: "math",
    sourceKeys: ["math-teacher", "3d-coords"],
    count: rawCommunityItems.filter(
      (i) => i.id.includes("math-teacher") || i.id.includes("3d-coords")
    ).length,
  },
  {
    id: "chemistry",
    sourceKeys: ["science-chemistry"],
    count: rawCommunityItems.filter((i) => i.id.includes("science-chemistry")).length,
  },
  {
    id: "cs",
    sourceKeys: ["flowcharts", "uml-diagrams"],
    count: rawCommunityItems.filter(
      (i) => i.id.includes("flowcharts") || i.id.includes("uml-diagrams")
    ).length,
  },
];

const CHINESE_CATEGORY_META: BaseSubjectCategoryMeta = {
  id: "chinese",
  sourceKeys: ["chinese-pedagogy"],
  count: rawCommunityItems.filter((i) => i.id.includes("chinese-pedagogy")).length,
};

export const STEM_SUBJECT_IDS: readonly SubjectCategoryId[] = [
  "physics",
  "math",
  "chemistry",
  "cs",
] as const;

export function getVisibleSubjectCategories(
  locale: LocaleCode = "vi"
): LibraryCategoryMeta[] {
  const baseCategories = isChineseLocale(locale)
    ? [...BASE_SUBJECT_CATEGORIES, CHINESE_CATEGORY_META]
    : [...BASE_SUBJECT_CATEGORIES];

  const labels = getTranslations(locale).hud.stemCategoryLabels;
  return baseCategories.map((cat) => ({
    ...cat,
    label: labels[cat.id].label,
  }));
}

export function getVisibleSubjectIds(
  locale: LocaleCode = "vi"
): SubjectCategoryId[] {
  return isChineseLocale(locale)
    ? [...STEM_SUBJECT_IDS, "chinese"]
    : [...STEM_SUBJECT_IDS];
}

export function getFilteredCommunityLibraries(
  selectedSubjectIds: readonly SubjectCategoryId[],
  locale: LocaleCode = "vi"
): LibraryItem[] {
  const activeSourceKeys = new Set<string>();
  const categories = isChineseLocale(locale)
    ? [...BASE_SUBJECT_CATEGORIES, CHINESE_CATEGORY_META]
    : BASE_SUBJECT_CATEGORIES;

  categories.forEach((cat) => {
    if (selectedSubjectIds.includes(cat.id)) {
      cat.sourceKeys.forEach((key) => activeSourceKeys.add(key));
    }
  });

  return rawCommunityItems
    .filter((item) =>
      Array.from(activeSourceKeys).some((key) => item.id.includes(key))
    )
    .map((item, idx) => ({
      id: item.id || `community-lib-item-${idx}`,
      status: "published" as const,
      elements: item.elements,
      name: item.name || `Community Shape #${idx + 1}`,
      created: item.created || Date.now(),
    }));
}

export interface LibraryElementBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly width: number;
  readonly height: number;
}

export function computeLibraryElementsBounds(
  elements: readonly unknown[]
): LibraryElementBounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const raw of elements) {
    const el = raw as Record<string, unknown>;
    if (!el || el.isDeleted === true) continue;
    const x = typeof el.x === "number" ? el.x : 0;
    const y = typeof el.y === "number" ? el.y : 0;
    const width = typeof el.width === "number" ? el.width : 0;
    const height = typeof el.height === "number" ? el.height : 0;

    if (Array.isArray(el.points) && el.points.length > 0) {
      for (const pt of el.points) {
        if (Array.isArray(pt) && typeof pt[0] === "number" && typeof pt[1] === "number") {
          const px = x + pt[0];
          const py = y + pt[1];
          if (px < minX) minX = px;
          if (py < minY) minY = py;
          if (px > maxX) maxX = px;
          if (py > maxY) maxY = py;
        }
      }
    } else {
      const x1 = Math.min(x, x + width);
      const y1 = Math.min(y, y + height);
      const x2 = Math.max(x, x + width);
      const y2 = Math.max(y, y + height);
      if (x1 < minX) minX = x1;
      if (y1 < minY) minY = y1;
      if (x2 > maxX) maxX = x2;
      if (y2 > maxY) maxY = y2;
    }
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
    return { minX: 0, minY: 0, maxX: 100, maxY: 100, width: 100, height: 100 };
  }

  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  return { minX, minY, maxX, maxY, width, height };
}

export function instantiateLibraryItemAtCenter(
  item: LibraryItem,
  centerX: number,
  centerY: number,
  /** Optional deterministic prefix used in unit tests to verify remapped IDs. */
  idPrefix?: string
): readonly OrderedExcalidrawElement[] {
  const sourceElements = (item.elements ?? []).filter((el) => {
    const rec = el as unknown as Record<string, unknown>;
    return Boolean(rec && rec.isDeleted !== true);
  });
  if (sourceElements.length === 0) return [];

  const bounds = computeLibraryElementsBounds(sourceElements);
  const currentCenterX = bounds.minX + bounds.width / 2;
  const currentCenterY = bounds.minY + bounds.height / 2;
  const dx = Math.round(centerX - currentCenterX);
  const dy = Math.round(centerY - currentCenterY);

  const prefix =
    idPrefix ?? `stem-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const elementIdMap = new Map<string, string>();
  const groupIdMap = new Map<string, string>();

  sourceElements.forEach((el, idx) => {
    const rec = el as unknown as Record<string, unknown>;
    if (typeof rec.id === "string") {
      elementIdMap.set(rec.id, `${prefix}-el-${idx}`);
    }
    if (Array.isArray(rec.groupIds)) {
      for (const gid of rec.groupIds) {
        if (typeof gid === "string" && !groupIdMap.has(gid)) {
          groupIdMap.set(gid, `${prefix}-grp-${groupIdMap.size}`);
        }
      }
    }
  });

  const now = Date.now();
  return sourceElements.map((el, idx) => {
    const rec = el as unknown as Record<string, unknown>;
    const oldId = typeof rec.id === "string" ? rec.id : `el-${idx}`;
    const newId = elementIdMap.get(oldId) ?? `${prefix}-el-${idx}`;
    const x = (typeof rec.x === "number" ? rec.x : 0) + dx;
    const y = (typeof rec.y === "number" ? rec.y : 0) + dy;

    const nextGroupIds = Array.isArray(rec.groupIds)
      ? rec.groupIds
          .filter((gid): gid is string => typeof gid === "string")
          .map((gid) => groupIdMap.get(gid) ?? gid)
      : [];

    const nextContainerId =
      typeof rec.containerId === "string"
        ? (elementIdMap.get(rec.containerId) ?? rec.containerId)
        : rec.containerId ?? null;

    const nextBoundElements = Array.isArray(rec.boundElements)
      ? rec.boundElements.map((be) => {
          if (be && typeof be === "object" && "id" in be) {
            const beRec = be as { id: string; type: string };
            return {
              ...beRec,
              id: elementIdMap.get(beRec.id) ?? beRec.id,
            };
          }
          return be;
        })
      : rec.boundElements ?? null;

    const remapBinding = (binding: unknown) => {
      if (!binding || typeof binding !== "object") return binding ?? null;
      const bRec = binding as Record<string, unknown>;
      if (typeof bRec.elementId === "string") {
        return {
          ...bRec,
          elementId: elementIdMap.get(bRec.elementId) ?? bRec.elementId,
        };
      }
      return binding;
    };

    return {
      ...rec,
      id: newId,
      x,
      y,
      groupIds: nextGroupIds,
      containerId: nextContainerId,
      boundElements: nextBoundElements,
      startBinding: remapBinding(rec.startBinding),
      endBinding: remapBinding(rec.endBinding),
      updated: now,
    } as unknown as OrderedExcalidrawElement;
  });
}
